import { Helmet } from "react-helmet-async";

const SITE_NAME = "Sofia Sands Realty";
const DEFAULT_DESCRIPTION = "Boutique luxury real estate brokerage in Dubai & Ras Al Khaimah. RERA 41793. Bay Views, Hayat Island from AED 1,730/sqft — 15 min from Wynn Al Marjan. Golden Visa eligible. AI concierge available 24/7.";
const DEFAULT_IMAGE = "https://www.sofiasandsrealty.ae/images/bay-views/hero.webp";
const SITE_URL = "https://www.sofiasandsrealty.ae";
const DEFAULT_KEYWORDS = "luxury real estate Dubai, RAK property investment, Hayat Island apartments, Bay Views RAK, Wynn casino real estate, Golden Visa UAE property, off-plan Dubai, RERA licensed broker, Sofia Sands Realty";

// Full organization schema — used by AI search engines for entity recognition
const ORG_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["RealEstateAgent", "Organization"],
      "@id": `${SITE_URL}/#organization`,
      "name": "Sofia Sands Realty",
      "alternateName": ["Sofia Sands", "Sofia Sands RE"],
      "url": SITE_URL,
      "logo": {
        "@type": "ImageObject",
        "url": `${SITE_URL}/sofia-logo.webp`,
        "width": 200,
        "height": 60,
      },
      "image": DEFAULT_IMAGE,
      "description": "Sofia Sands Realty is a boutique luxury real estate brokerage operating under RERA license 41793 in Dubai and Ras Al Khaimah, UAE. We represent a curated roster of buyers — end-users, yield-focused investors, and Golden Visa applicants — and match them to off-market and premium on-market inventory. Our flagship allocation is Bay Views on Hayat Island, RAK: one full floor of 12 waterfront residences offered as a single transaction at AED 16,200,000 (AED 1,730/sqft), 15 minutes from the forthcoming Wynn Al Marjan Casino Resort.",
      "telephone": "+971547054704",
      "email": "hello@sofiasandsrealty.ae",
      "foundingDate": "2020",
      // address + geo live ONCE, in public/index.html (same @id #organization) — 2026-10-31
      "areaServed": [
        { "@type": "City", "name": "Dubai", "sameAs": "https://www.wikidata.org/wiki/Q612" },
        { "@type": "City", "name": "Ras Al Khaimah", "sameAs": "https://www.wikidata.org/wiki/Q193283" },
        { "@type": "Place", "name": "Hayat Island, RAK" },
        { "@type": "Place", "name": "Hayat Island" },
        { "@type": "Place", "name": "Al Marjan Island" },
        { "@type": "Place", "name": "Mina Al Arab" },
      ],
      "hasCredential": {
        "@type": "EducationalOccupationalCredential",
        "credentialCategory": "Real Estate Regulatory Authority License",
        "name": "RERA License 41793",
        "recognizedBy": { "@type": "GovernmentOrganization", "name": "Dubai Land Department (DLD)", "url": "https://dubailand.gov.ae/" },
      },
      "knowsAbout": [
        "Dubai luxury real estate",
        "RAK off-plan property investment",
        "Hayat Island waterfront residences",
        "UAE Golden Visa property",
        "Wynn Al Marjan Casino real estate",
        "AI-powered real estate concierge",
      ],
      "employee": {
        "@type": "Person",
        "@id": `${SITE_URL}/about/yitayal-mesfin#person`,
        "name": "Yitayal Mesfin",
        "jobTitle": "Founder & Principal Broker",
        "url": `${SITE_URL}/about/yitayal-mesfin`,
        "sameAs": ["https://www.instagram.com/ytdubai", "https://twitter.com/ytdubai", "https://uae.dubizzle.com/property-agents/yitayal-mesfin-201490/"],
        "award": ["RAK Properties Brokers Awards 2024 — Top 3 Agency (A1 Properties)", "Prestigious Awards 2023 — Winner"],
        "telephone": "+971547054704",
        "email": "yt@sofiasandsrealty.ae",
        "hasCredential": { "name": "RERA License 41793" },
      },
      "sameAs": [
        "https://www.instagram.com/sofia_sands_realty",
        "https://www.linkedin.com/company/sofia-sands-realty/",
        "https://twitter.com/ytdubai",
        "https://www.facebook.com/sofiasandsrealty/",
        "https://www.bayut.com/companies/sofia-sands-realty-104473/",
        "https://uae.dubizzle.com/property-agencies/sofia-sands-realty-9301/",
        "https://maps.google.com/?cid=6634131096182007739",
      ],
      "parentOrganization": { "@type": "Organization", "name": "Beit Nour Group", "url": "https://beitnourgroup.com/" },
      "priceRange": "AED 1,100,000 – AED 16,200,000",
      "currenciesAccepted": "AED, USD",
      "paymentAccepted": "Bank Transfer, Crypto",
      "openingHours": "Mo-Su 00:00-24:00",
    },
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      "url": SITE_URL,
      "name": "Sofia Sands Realty",
      "description": DEFAULT_DESCRIPTION,
      "publisher": { "@id": `${SITE_URL}/#organization` },
      "potentialAction": {
        "@type": "SearchAction",
        "target": { "@type": "EntryPoint", "urlTemplate": `${SITE_URL}/listings?q={search_term_string}` },
        "query-input": "required name=search_term_string",
      },
      "inLanguage": ["en", "ar", "ru"],
    },
    {
      "@type": "FAQPage",
      "@id": `${SITE_URL}/#faq`,
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is the price per sqft at Bay Views Hayat Island RAK?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Bay Views on Hayat Island, Ras Al Khaimah is AED 1,730 per sqft: AED 16,200,000 for the whole of floor 9 (12 waterfront residences, 9,364 sqft), sold as one lot with no per-unit sales. For context, Dubai Marina/JBR registered a median of about AED 4,448 per sqft in September 2026 (Dubai Land Department data on our market pulse).",
          },
        },
        {
          "@type": "Question",
          "name": "How close is Bay Views to the Wynn Al Marjan Casino?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Bay Views on Hayat Island is approximately 12 to 15 minutes by car from Wynn Al Marjan Island, which Wynn Resorts has scheduled to open in September 2027 (second-quarter 2026 results, 4 August 2026). We do not publish a price forecast for the opening; underwrite on today's rents.",
          },
        },
        {
          "@type": "Question",
          "name": "Is Sofia Sands Realty RERA licensed?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Sofia Sands Realty operates under RERA License 41793, issued by the Dubai Land Department (DLD). Dubai properties are registered with the Dubai Land Department; Ras Al Khaimah properties such as Bay Views and Bay Residences are registered with RAK Municipality (Land Department). All transactions are handled by RERA-certified brokers.",
          },
        },
        {
          "@type": "Question",
          "name": "Does Bay Views qualify for the UAE Golden Visa?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Property of AED 2,000,000 and above qualifies for the UAE 10-year Golden Visa, so the Bay Views full floor at AED 16,200,000 qualifies comfortably, providing long-term UAE residency for the buyer and their immediate family.",
          },
        },
      ],
    },
  ],
};

export default function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  url,
  type = "website",
  keywords = DEFAULT_KEYWORDS,
  noindex = false,
  schema,
  breadcrumbs,
}) {
  const fullTitle = title
    ? `${title} | ${SITE_NAME}`
    : `${SITE_NAME} | Luxury Real Estate Dubai & RAK | RERA 41793`;
  const fullUrl = url ? `${SITE_URL}${url}` : SITE_URL;
  const fullImage = image.startsWith("http") ? image : `${SITE_URL}${image}`;

  // 2026-11-04: ORG_SCHEMA's FAQPage is the HOME page FAQ — only emit it on "/".
  // It was going out on every route without its own schema (/about, /privacy-policy…),
  // i.e. FAQ markup for questions not on the page, and doubled FAQPage on the home snapshot.
  const isHome = !url || url === "/";
  const siteSchema = isHome ? ORG_SCHEMA
    : { ...ORG_SCHEMA, "@graph": ORG_SCHEMA["@graph"].filter((n) => n["@type"] !== "FAQPage") };
  const schemas = [schema || siteSchema];

  if (breadcrumbs) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((b, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "name": b.name,
        "item": `${SITE_URL}${b.path}`,
      })),
    });
  }

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="Sofia Sands Realty" />
      <meta name="robots" content={noindex ? "noindex,nofollow" : "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1"} />
      <meta name="theme-color" content="#0B2027" />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:image:width" content={fullImage.includes("/images/og/") ? "1200" : "1280"} />
      <meta property="og:image:height" content={fullImage.includes("/images/og/") ? "630" : "720"} />
      <meta property="og:image:alt" content={title || "Sofia Sands Realty — Luxury Real Estate Dubai & RAK"} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_AE" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@ytdubai" />
      <meta name="twitter:creator" content="@ytdubai" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />

      {/* Geo targeting — UAE */}
      <meta name="geo.region" content="AE" />
      <meta name="geo.placename" content="Dubai, United Arab Emirates" />
      <meta name="ICBM" content="25.2048, 55.2708" />

      {/* Canonical — always www */}
      <link rel="canonical" href={fullUrl} />

      {/* Hreflang — multilingual signals for AI + Google */}
      <link rel="alternate" hrefLang="en" href={fullUrl} />
      {url === "/bay-views" && <link rel="alternate" hrefLang="ar" href={`${SITE_URL}/ar/bay-views`} />}
      {url === "/bay-views" && <link rel="alternate" hrefLang="ru" href={`${SITE_URL}/ru/bay-views`} />}
      {url === "/ar/bay-views" && <link rel="alternate" hrefLang="en" href={`${SITE_URL}/bay-views`} />}
      {url === "/ar/bay-views" && <link rel="alternate" hrefLang="ar" href={`${SITE_URL}/ar/bay-views`} />}
      {url === "/ru/bay-views" && <link rel="alternate" hrefLang="en" href={`${SITE_URL}/bay-views`} />}
      {url === "/ru/bay-views" && <link rel="alternate" hrefLang="ru" href={`${SITE_URL}/ru/bay-views`} />}
      {/* x-default = the English page itself (was hard-wired to /bay-views on every route — GSC audit 2026-10-22) */}
      <link rel="alternate" hrefLang="x-default" href={url === "/ar/bay-views" || url === "/ru/bay-views" ? `${SITE_URL}/bay-views` : fullUrl} />

      {/* Structured Data */}
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
}
